"use client";

import { Icons } from "@/components/icons";
import { siteConfig } from "@/config/site";
import { freePlan, basicPlan, proPlan } from "@/config/subscriptions";
import {
  AnimatedSection,
  StaggerGrid,
  FadeInChild,
} from "./motion";

/* ─── Plan types ─── */
interface PlanCard {
  name: string;
  price: string;
  subtitle: string;
  features: string[];
  highlighted?: boolean;
  checkColor: string;
}

const PLANS: PlanCard[] = [
  {
    name: freePlan.name,
    price: `₹${freePlan.price}`,
    subtitle: "Perfect for trying things out.",
    checkColor: "text-emerald-500",
    features: [
      `${freePlan.maxChatbots} Chatbot`,
      `${freePlan.maxCrawlers} Crawlers`,
      `${freePlan.maxFiles} Files`,
      `${freePlan.maxMessagesPerMonth} Messages/mo`,
      "API Access",
    ],
  },
  {
    name: basicPlan.name,
    price: `₹${basicPlan.price}`,
    subtitle: "For teams deploying multiple bots.",
    checkColor: "text-emerald-500",
    features: [
      `${basicPlan.maxChatbots} Chatbots`,
      `${basicPlan.maxCrawlers} Crawlers`,
      "Unlimited Messages",
      "Platform Integrations",
      "Lead Collection",
      "Webhooks",
    ],
  },
  {
    name: proPlan.name,
    price: `₹${proPlan.price}`,
    subtitle: "Advanced features, full power.",
    highlighted: true,
    checkColor: "text-indigo-500",
    features: [
      `${proPlan.maxChatbots} Chatbots`,
      "All Integrations",
      "Unlimited Messages",
      "Advanced Analytics",
      `Remove 'Powered by ${siteConfig.name}'`,
      "File Attachments",
      "Priority Support",
    ],
  },
  {
    name: "Enterprise",
    price: "Custom",
    subtitle: "White-label, SLA & custom models.",
    checkColor: "text-emerald-500",
    features: [
      "Unlimited Everything",
      "Custom AI Models",
      "SSO & RBAC",
      "Dedicated Support",
      "SLA Guarantee",
    ],
  },
];

export function PricingSection() {
  return (
    <AnimatedSection id="pricing" className="container py-16 md:py-24 lg:py-32">
      <div className="px-4 md:px-6">
        <div className="mx-auto flex max-w-[58rem] flex-col items-center justify-center gap-4 text-center mb-12">
          <span className="inline-flex items-center rounded-full bg-pink-50 px-4 py-1 text-xs font-semibold text-pink-600 ring-1 ring-inset ring-pink-200">
            Pricing
          </span>
          <h2 className="text-3xl font-bold tracking-tight sm:text-5xl">
            Pick the perfect plan for you
          </h2>
          <p className="max-w-[800px] text-muted-foreground md:text-lg">
            All plans include multi-model AI, platform integrations, REST API, website builder and
            analytics.
          </p>
        </div>

        <StaggerGrid className="mx-auto grid max-w-sm items-start gap-8 sm:max-w-4xl sm:grid-cols-2 lg:max-w-5xl lg:grid-cols-4">
          {PLANS.map((plan) => (
            <FadeInChild key={plan.name}>
              <div
                className={
                  plan.highlighted
                    ? "relative flex flex-col rounded-2xl border-2 border-indigo-500 bg-white p-6 shadow-lg shadow-indigo-500/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-indigo-500/20"
                    : "flex flex-col rounded-2xl border border-slate-200/60 bg-white/80 backdrop-blur-sm p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                }
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center rounded-full bg-indigo-600 px-3 py-1 text-xs font-semibold text-white shadow-sm">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="space-y-3 mb-6">
                  <h3 className="text-xl font-bold tracking-tight">{plan.name}</h3>
                  <div className="flex items-baseline gap-1">
                    <span
                      className={`text-3xl font-bold ${plan.highlighted ? "text-indigo-600" : ""}`}
                    >
                      {plan.price}
                    </span>
                    {plan.price !== "Custom" && (
                      <span className="text-sm text-muted-foreground">/mo</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{plan.subtitle}</p>
                </div>
                <div className={`h-px mb-6 ${plan.highlighted ? "bg-indigo-100" : "bg-slate-100"}`} />
                <ul className="grid gap-3 text-sm">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <Icons.check className={`h-4 w-4 shrink-0 ${plan.checkColor}`} />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeInChild>
          ))}
        </StaggerGrid>
      </div>
    </AnimatedSection>
  );
}
